import { Typography, Box, useTheme, Tooltip } from "@mui/material";
import MaterialSearchInput from "../../../components/input/MaterialSearchInput";
import config from "../../../config";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import { FiPlus, FiMinus, FiRefreshCw } from "react-icons/fi";
import { LuDownload, LuFilter } from "react-icons/lu";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import AgGrid from "../../../components/grid/AgGrid";
import { centerAlignColDef, defaultColDef, floatFormater, formatTo2Decimals, leftAlignColDef, rightAlignColDef } from "../../../components/grid/columnDefinitions/columnDefinitions";
import GridSelectEditor from "../../../components/grid/GridSelectEditor";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import { Corporate } from "../../../db_access/corporate";
import { Emission_detail } from "../../../db_access/emission_details";
import { Structure } from "../../../db_access/structure";
import { Supply_chain } from "../../../db_access/supply_chain";
import { Product } from "../../../db_access/product";
import { supplychainEmissionDetailMap } from "../ScopeCategory";
import { Organization, ValueChain } from "./CalculationMethod";
import { DataTableCard, M3Button, M3IconButton } from "../../../components/m3";
import ColumnMenu from "../../../components/context-menu/ColumnMenu";
import { findEmissionOptionMatches } from "../../../components/page-context/Helpers/buildOptions";
import { Convert, ConvertFuel, GetUnits } from "../../../components/Unit";

// Organisation-only Type option: sources Description from the workspace's supply chains
// (as opposed to "By Product Supplier", which sources Description from the Products module).
const SUPPLY_CHAIN_TYPE = "By Supply Chain";

const attachPollutantFactorsAndTotals = (row, match) => {
  const qty = (row.quantity !== null && row.quantity !== undefined && row.quantity !== "") ? (isNaN(parseFloat(row.quantity)) ? 0 : parseFloat(row.quantity)) : 0;
  const factor = (row.factor !== null && row.factor !== undefined && row.factor !== "") ? (isNaN(parseFloat(row.factor)) ? 1.0 : parseFloat(row.factor)) : 1.0;
  const uncertainty = (row.uncertainty !== null && row.uncertainty !== undefined && row.uncertainty !== "") ? (isNaN(parseFloat(row.uncertainty)) ? 1.0 : parseFloat(row.uncertainty)) : 1.0;
  const multiplier = qty * factor * uncertainty;

  let unitScale = 1.0;
  if (match && match.unit && row.unit && match.unit.toLowerCase().trim() !== row.unit.toLowerCase().trim()) {
    try {
      const conv = Convert(1, row.unit, match.unit);
      if (conv && !isNaN(conv) && conv > 0) {
        unitScale = conv;
      }
    } catch (e) {
      unitScale = 1.0;
    }
  }

  if (match) {
    row.co2_factor = ((match.co2 !== null && match.co2 !== undefined && match.co2 !== "") ? parseFloat(match.co2) : (row.co2_factor || 0)) * unitScale;
    row.ch4_factor = ((match.ch4 !== null && match.ch4 !== undefined && match.ch4 !== "") ? parseFloat(match.ch4) : (row.ch4_factor || 0)) * unitScale;
    row.n2o_factor = ((match.n2o !== null && match.n2o !== undefined && match.n2o !== "") ? parseFloat(match.n2o) : (row.n2o_factor || 0)) * unitScale;
    row.nox_factor = ((match.nox !== null && match.nox !== undefined && match.nox !== "") ? parseFloat(match.nox) : (row.nox_factor || 0)) * unitScale;
    row.pm25_factor = ((match.pm25 !== null && match.pm25 !== undefined && match.pm25 !== "") ? parseFloat(match.pm25) : (row.pm25_factor || 0)) * unitScale;
    row.pm10_factor = ((match.pm10 !== null && match.pm10 !== undefined && match.pm10 !== "") ? parseFloat(match.pm10) : (row.pm10_factor || 0)) * unitScale;
    row.black_carbon_factor = ((match.black_carbon !== null && match.black_carbon !== undefined && match.black_carbon !== "") ? parseFloat(match.black_carbon) : (row.black_carbon_factor || 0)) * unitScale;
  }

  row.total_co2 = multiplier * (row.co2_factor || 0);
  row.total_ch4 = multiplier * (row.ch4_factor || 0);
  row.total_n2o = multiplier * (row.n2o_factor || 0);
  row.total_nox = multiplier * (row.nox_factor || 0);
  row.total_pm25 = multiplier * (row.pm25_factor || 0);
  row.total_pm10 = multiplier * (row.pm10_factor || 0);
  row.total_black_carbon = multiplier * (row.black_carbon_factor || 0);

  return row;
};

const EmissionDetailGrid = ({ organizationType, emissionID, emissionCategory, emissionDetails, isBiogenic, isAvoided, onInventoryTotalChange, onInventoryBiogenicTotalChange, onPendingEditsChange, saveRef, readOnly = false, validationStatus = "Not Validated" }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { refresh, setRefresh, setIsSaveVisible, setIsLoading, columnVisibilities, emissionDetailOptions } = useContextProvider();
  const [localEmissionDetails, setLocalEmissionDetails] = useState([]);
  const localEmissionDetailsRef = useRef([]);
  useEffect(() => { localEmissionDetailsRef.current = localEmissionDetails; }, [localEmissionDetails]);
  const [selectedEmissionDetails, setSelectedEmissionDetails] = useState([]);
  const [detailIdsToBeDeleted, setDetailIdsToBeDeleted] = useState([]);
  const [supplierData, setSupplierData] = useState([]);
  const supplierDataRef = useRef([]);
  const workspaceSupplierDataRef = useRef([]);
  const calculationMethodsRef = useRef([]);

  useEffect(() => {
    Product.get_mine()
      .then((response) => response.json())
      .then((data) => {
        setSupplierData(data || []);
        supplierDataRef.current = data || [];
      })
      .catch((error) => console.error("Error fetching supplier products:", error));
  }, []);

  const gridRef = useRef(null);
  const gridHeaderContainerRef = useRef(null);
  const [columnMenuVisible, setColumnMenuVisible] = useState(false);
  const [newlyAddedId, setNewlyAddedId] = useState(null);
  const emissionDetailOptionsRef = useRef({});
  useEffect(() => {
    emissionDetailOptionsRef.current = emissionDetailOptions || {};
  }, [emissionDetailOptions]);
  const colVisibility_key = "emission_detail_grid_columns_v2";

  const calculationMethods = useMemo(() => {
    if (emissionCategory) {
      let methods = Object.keys(supplychainEmissionDetailMap?.[emissionCategory] || {});
      switch (organizationType) {
        case Supply_chain.label: return methods;
        case Structure.label: return [...methods, ValueChain];
        case Corporate.label: return [...methods, ValueChain, Organization];
        default: return [];
      }
    }
    if (organizationType === Supply_chain.label) {
      return [ValueChain.label];
    }
    return [ValueChain.label, Organization.label];
  }, [emissionCategory, organizationType]);

  useEffect(() => { calculationMethodsRef.current = calculationMethods; }, [calculationMethods]);

  const newEmissionDetail = useCallback(() => {
    if (readOnly || !emissionID) return;
    const newId = uuidv4().replace(/-/g, "");
    setNewlyAddedId(newId);

    const catStr = String(emissionCategory || "").toLowerCase();
    let initialType = "";
    let initialCat = "";
    let initialSubCat = "";
    let initialDesc = "";
    let initialUnit = "";
    let initialGwp = 0;

    if (catStr.includes("electric")) {
      initialType = "Energy sources";
      initialCat = "Electricity";
      initialSubCat = "Grid Mix (Canada)";
      initialDesc = "Canada Average";
      initialUnit = "kWh";
      initialGwp = 0.224;
    } else if (catStr.includes("stationary") || catStr.includes("combustion")) {
      initialType = "Energy sources";
      initialCat = "Fossil Fuels";
      initialSubCat = "Natural & Industrial Gas";
      initialDesc = "Natural gas";
      initialUnit = "kWh";
      initialGwp = 0.21;
    } else if (catStr.includes("mobile") || catStr.includes("transport")) {
      initialType = "Transport";
      initialCat = "Freight Transport";
      initialSubCat = "Road Freight";
      initialDesc = "Diesel-Powered (generic baseline)";
      initialUnit = "L";
      initialGwp = 2.68;
    }

    const defaultQty = initialDesc ? 1 : null;
    const defaultTotal = defaultQty && initialGwp ? defaultQty * initialGwp : 0;

    let rowObj = {
      id: newId,
      emission_id: emissionID,
      calculation_method: calculationMethods[0] || (initialType === "Transport" ? "Fuel based" : "Location based"),
      type: initialType,
      category: initialCat,
      sub_category: initialSubCat,
      description: initialDesc,
      quantity: defaultQty,
      unit: initialUnit,
      gwp_unit: initialGwp,
      factor: 1.0,
      uncertainty: 1.0,
      totalco2eq: defaultTotal,
      biogenic_total: 0,
      _isNew: true,
      _isModified: true,
    };

    if (initialDesc) {
      const currentOpts = emissionDetailOptionsRef.current || emissionDetailOptions || {};
      const matches = findEmissionOptionMatches(currentOpts, initialType, initialCat, initialSubCat, initialDesc);
      const match = matches.find((m) => m.unit?.toLowerCase().trim() === initialUnit?.toLowerCase().trim()) || matches[0];
      rowObj = attachPollutantFactorsAndTotals(rowObj, match);
    }

    setLocalEmissionDetails((prev) => [...prev, rowObj]);
    setIsSaveVisible(true);
  }, [emissionID, readOnly, emissionCategory, calculationMethods, emissionDetailOptions, setIsSaveVisible]);

  const deleteSelected = useCallback(() => {
    if (readOnly) return;
    const ids = selectedEmissionDetails.map(ed => ed.id);
    if (!ids.length) return;
    const newDetailIDs = new Set(localEmissionDetails.filter(ed => ed._isNew).map(ed => ed.id));
    const savedIDs = ids.filter(id => !newDetailIDs.has(id));

    setLocalEmissionDetails((prev) => prev.filter((ed) => !ids.includes(ed.id)));
    if (savedIDs.length > 0) {
      setDetailIdsToBeDeleted((prev) => [...prev, ...savedIDs]);
    }
    setSelectedEmissionDetails([]);
    setIsSaveVisible(true);
  }, [readOnly, selectedEmissionDetails, localEmissionDetails, setIsSaveVisible]);

  const pollutantValueFormatter = (params) => {
    if (params.value === null || params.value === undefined || params.value === "" || isNaN(Number(params.value))) return "-";
    const val = Number(params.value);
    if (val === 0) return "-";
    if (Math.abs(val) >= 0.01) {
      return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return Number(val.toFixed(4)).toString();
  };

  const getPollutantTotal = useCallback((params, pollutantKey) => {
    const data = params.data;
    if (!data) return null;

    const qty = (data.quantity !== null && data.quantity !== undefined && data.quantity !== "") ? (isNaN(parseFloat(data.quantity)) ? 0 : parseFloat(data.quantity)) : 0;
    const factor = (data.factor !== null && data.factor !== undefined && data.factor !== "") ? (isNaN(parseFloat(data.factor)) ? 1.0 : parseFloat(data.factor)) : 1.0;
    const uncertainty = (data.uncertainty !== null && data.uncertainty !== undefined && data.uncertainty !== "") ? (isNaN(parseFloat(data.uncertainty)) ? 1.0 : parseFloat(data.uncertainty)) : 1.0;
    const multiplier = qty * factor * uncertainty;

    // 1. Direct row value if already attached
    const directTotal = data[`total_${pollutantKey}`];
    if (directTotal !== undefined && directTotal !== null && !isNaN(directTotal) && Number(directTotal) > 0) {
      return Number(directTotal);
    }

    const directFactor = data[`${pollutantKey}_factor`];
    if (directFactor !== undefined && directFactor !== null && !isNaN(directFactor) && Number(directFactor) > 0) {
      return multiplier * Number(directFactor);
    }

    // 2. Fallback to dictionary lookup
    const { type, category, sub_category, description } = data;
    const currentOpts = emissionDetailOptionsRef.current || emissionDetailOptions || {};
    const matches = findEmissionOptionMatches(currentOpts, type, category, sub_category, description);
    const match = (data.unit ? matches.find((m) => m.unit?.toLowerCase().trim() === data.unit?.toLowerCase().trim()) : null) || matches[0];
    if (!match) return null;

    const rawVal = match[pollutantKey] ?? match[`${pollutantKey}_factor`];
    if (rawVal === null || rawVal === undefined || rawVal === "") return null;
    const unitVal = parseFloat(rawVal);
    if (isNaN(unitVal) || unitVal <= 0) return null;

    return multiplier * unitVal;
  }, [emissionDetailOptions]);

  const onSearchSelect = useCallback(async (material) => {
    if (readOnly || !emissionID) return;

    const default_gwp = parseFloat(material.gwp_per_unit) || 0;

    if (selectedEmissionDetails.length > 0) {
      const selectedId = selectedEmissionDetails[0].id;
      setLocalEmissionDetails((prev) =>
        prev.map((ed) => {
          if (ed.id === selectedId) {
            const hasQty = ed.quantity !== null && ed.quantity !== undefined && ed.quantity !== "";
            const qty = hasQty ? parseFloat(ed.quantity) : 1;
            const factor = (ed.factor !== null && ed.factor !== undefined && ed.factor !== "") ? parseFloat(ed.factor) : 1.0;
            const uncertainty = (ed.uncertainty !== null && ed.uncertainty !== undefined && ed.uncertainty !== "") ? parseFloat(ed.uncertainty) : 1.0;
            const total = qty * factor * uncertainty * default_gwp;
            const isBiogenicRow = material.sub_category?.toLowerCase().includes('biogenic') && default_gwp < 0;

            const updated = {
              ...ed,
              type: material.type,
              category: material.category,
              sub_category: material.sub_category,
              description: material.description,
              unit: material.unit || "",
              quantity: hasQty ? ed.quantity : 1,
              factor: (ed.factor !== null && ed.factor !== undefined && ed.factor !== "") ? ed.factor : 1.0,
              uncertainty: (ed.uncertainty !== null && ed.uncertainty !== undefined && ed.uncertainty !== "") ? ed.uncertainty : 1.0,
              gwp_unit: default_gwp,
              totalco2eq: isBiogenicRow ? 0 : total,
              biogenic_total: isBiogenicRow ? total : 0,
              _isModified: true
            };
            return attachPollutantFactorsAndTotals(updated, material);
          }
          return ed;
        })
      );
      setIsSaveVisible(true);
    } else {
      setIsLoading(true);
      try {
        const response = await Emission_detail.new(emissionID);
        const resData = await response.json();
        if (resData && resData.id) {
          const newId = resData.id;
          const isBiogenicRow = material.sub_category?.toLowerCase().includes('biogenic') && default_gwp < 0;
          let newRow = {
            id: newId,
            emission_id: emissionID,
            calculation_method: calculationMethods[0] || "Value Chain",
            type: material.type,
            category: material.category,
            sub_category: material.sub_category,
            description: material.description,
            unit: material.unit || "",
            quantity: 1,
            factor: 1.0,
            uncertainty: 1.0,
            gwp_unit: default_gwp,
            totalco2eq: isBiogenicRow ? 0 : default_gwp,
            biogenic_total: isBiogenicRow ? default_gwp : 0,
            _isModified: true
          };
          newRow = attachPollutantFactorsAndTotals(newRow, material);

          await Emission_detail.update([newRow]);

          setRefresh((r) => !r);
        }
      } catch (err) {
        console.error("Error adding search selected material:", err);
      } finally {
        setIsLoading(false);
      }
    }
  }, [emissionID, selectedEmissionDetails, calculationMethods, setIsLoading, setRefresh, setIsSaveVisible]);

  const rawColumnDefs = useMemo(() => [
    {
      headerName: "Calculation Method", field: "calculation_method", cellEditor: GridSelectEditor,
      ...defaultColDef, ...leftAlignColDef, editable: true, resizable: true,
      cellEditorParams: () => ({ values: calculationMethodsRef.current })
    },
    {
      headerName: "Type", field: "type", cellEditor: GridSelectEditor,
      ...defaultColDef, ...leftAlignColDef, editable: true, resizable: true,
      cellEditorParams: (params) => {
        const supplierTypes = organizationType === Supply_chain.label
          ? ["User Entry", "By Product Supplier"]
          : ["User Entry", "By Product Supplier", SUPPLY_CHAIN_TYPE];
        const currentOpts = emissionDetailOptionsRef.current || emissionDetailOptions || {};
        const types = [...supplierTypes, ...Object.keys(currentOpts).sort()];
        const onValueChanged = (value) => { params.node.setDataValue("type", value); };
        return { values: types, onCellValueChanged: onValueChanged };
      }
    },
    {
      headerName: "Category", field: "category", ...defaultColDef, ...leftAlignColDef, editable: true, resizable: true,
      cellEditorSelector: (params) => {
        if (params.data.type === "User Entry") {
          return { component: "agTextCellEditor" };
        }
        return {
          component: GridSelectEditor,
          params: {
            values: (() => {
              const type = params.data.type;
              let categories = [];
              if (type === "By Product Supplier" || type === SUPPLY_CHAIN_TYPE) {
                categories = ["Virgin Product", "Reused Product"];
              } else {
                const currentOpts = emissionDetailOptionsRef.current || emissionDetailOptions || {};
                categories = type && currentOpts?.[type] ? Object.keys(currentOpts[type]).sort() : [];
              }
              return categories;
            })(),
            onCellValueChanged: (value) => { params.node.setDataValue("category", value); }
          }
        };
      }
    },
    {
      headerName: "Sub Category", field: "sub_category", ...defaultColDef, ...leftAlignColDef, editable: true, resizable: true,
      cellEditorSelector: (params) => {
        if (params.data.type === "User Entry") {
          return { component: "agTextCellEditor" };
        }
        return {
          component: GridSelectEditor,
          params: {
            values: (() => {
              const { type, category } = params.data;
              let subcats = [];
              if (type === "By Product Supplier" || type === SUPPLY_CHAIN_TYPE) {
                subcats = [];
              } else {
                const currentOpts = emissionDetailOptionsRef.current || emissionDetailOptions || {};
                subcats = type && category && currentOpts?.[type]?.[category] ? Object.keys(currentOpts[type][category]).sort() : [];
              }
              return subcats;
            })(),
            onCellValueChanged: (value) => { params.node.setDataValue("sub_category", value); }
          }
        };
      }
    },
    {
      headerName: "Description", field: "description", ...defaultColDef, ...leftAlignColDef, flex: 1.5, editable: true, resizable: true,
      cellEditorSelector: (params) => {
        if (params.data.type === "User Entry") {
          return { component: "agTextCellEditor" };
        }
        return {
          component: GridSelectEditor,
          params: {
            values: (() => {
              const { type, category, sub_category } = params.data;
              let descriptions = [];
              if (type === "By Product Supplier") {
                descriptions = supplierDataRef.current.map((item) => (item.supplier ? `${item.supplier}_${item.name}` : item.name)).sort();
              } else if (type === SUPPLY_CHAIN_TYPE) {
                descriptions = workspaceSupplierDataRef.current.map((item) => item.name).filter(Boolean).sort();
              } else {
                const currentOpts = emissionDetailOptionsRef.current || emissionDetailOptions || {};
                descriptions = type && category && currentOpts?.[type]?.[category]?.[sub_category || ""]
                  ? Object.keys(currentOpts[type][category][sub_category || ""]).sort()
                  : [];
              }
              return descriptions;
            })(),
            onCellValueChanged: (value) => { params.node.setDataValue("description", value); }
          }
        };
      }
    },
    { headerName: "Quantity", field: "quantity", ...defaultColDef, ...rightAlignColDef, ...floatFormater, width: 120, minWidth: 120, editable: true, resizable: true },
    {
      headerName: "Unit", field: "unit", ...defaultColDef, ...rightAlignColDef, width: 100, minWidth: 100, editable: true, resizable: true,
      cellEditorSelector: (params) => {
        if (params.data.type === "User Entry") {
          return { component: "agTextCellEditor" };
        }
        const { type, category, sub_category, description, unit } = params.data;
        const currentOpts = emissionDetailOptionsRef.current || emissionDetailOptions || {};
        const matches = (type && category && description && currentOpts?.[type]?.[category]?.[sub_category || ""]?.[description]) || [];
        const dbUnits = matches.map((m) => m.unit).filter(Boolean);

        const descLower = String(description || "").toLowerCase();
        const catLower = String(category || "").toLowerCase();
        const subCatLower = String(sub_category || "").toLowerCase();

        let extraUnits = [];
        if (descLower.includes("natural gas") || catLower.includes("fossil fuels")) {
          extraUnits = ["kWh", "m³", "GJ", "mmBTU"];
        } else if (descLower.includes("diesel") || subCatLower.includes("road freight") || catLower.includes("transport")) {
          extraUnits = ["L", "gal", "gal (US)"];
        } else if (catLower.includes("electricity") || subCatLower.includes("grid mix")) {
          extraUnits = ["kWh", "MWh", "GJ"];
        } else if (dbUnits.length > 0) {
          extraUnits = GetUnits(dbUnits[0]) || [];
        }

        const candidateUnits = Array.from(new Set([...dbUnits, ...extraUnits, unit].filter(Boolean)));
        if (candidateUnits.length > 1) {
          return {
            component: GridSelectEditor,
            params: {
              values: candidateUnits,
              onCellValueChanged: (value) => { params.node.setDataValue("unit", value); }
            }
          };
        }
        return { component: "agTextCellEditor" };
      }
    },
    { headerName: "GWP per Unit", field: "gwp_unit", ...defaultColDef, ...rightAlignColDef, ...floatFormater, width: 120, minWidth: 120, editable: (params) => params.data.type === "User Entry", resizable: true },
    { headerName: "Factor", field: "factor", ...defaultColDef, ...rightAlignColDef, ...floatFormater, width: 100, minWidth: 100, editable: true, resizable: true },
    { headerName: "Uncertainty", field: "uncertainty", ...defaultColDef, ...rightAlignColDef, ...floatFormater, width: 120, minWidth: 150, editable: true, resizable: true },
    { headerName: "Total (kg CO₂e)", field: "totalco2eq", ...defaultColDef, ...rightAlignColDef, ...floatFormater, width: 150, minWidth: 150, editable: false, resizable: true },
    { headerName: "Total CO₂ (kg)", field: "total_co2", ...defaultColDef, ...rightAlignColDef, valueGetter: (params) => getPollutantTotal(params, "co2"), valueFormatter: pollutantValueFormatter, width: 140, minWidth: 130, editable: false, resizable: true },
    { headerName: "Total CH₄ (kg)", field: "total_ch4", ...defaultColDef, ...rightAlignColDef, valueGetter: (params) => getPollutantTotal(params, "ch4"), valueFormatter: pollutantValueFormatter, width: 140, minWidth: 130, editable: false, resizable: true },
    { headerName: "Total N₂O (kg)", field: "total_n2o", ...defaultColDef, ...rightAlignColDef, valueGetter: (params) => getPollutantTotal(params, "n2o"), valueFormatter: pollutantValueFormatter, width: 140, minWidth: 130, editable: false, resizable: true },
    { headerName: "Total NOx (kg)", field: "total_nox", ...defaultColDef, ...rightAlignColDef, valueGetter: (params) => getPollutantTotal(params, "nox"), valueFormatter: pollutantValueFormatter, width: 140, minWidth: 130, editable: false, resizable: true },
    { headerName: "Total PM2.5 (kg)", field: "total_pm25", ...defaultColDef, ...rightAlignColDef, valueGetter: (params) => getPollutantTotal(params, "pm25"), valueFormatter: pollutantValueFormatter, width: 140, minWidth: 130, editable: false, resizable: true },
    { headerName: "Total PM10 (kg)", field: "total_pm10", ...defaultColDef, ...rightAlignColDef, valueGetter: (params) => getPollutantTotal(params, "pm10"), valueFormatter: pollutantValueFormatter, width: 140, minWidth: 130, editable: false, resizable: true },
    { headerName: "Total Black Carbon (kg)", field: "total_black_carbon", ...defaultColDef, ...rightAlignColDef, valueGetter: (params) => getPollutantTotal(params, "black_carbon"), valueFormatter: pollutantValueFormatter, width: 160, minWidth: 150, editable: false, resizable: true },
    { headerName: "Biogenic Total (kg CO₂e)", field: "biogenic_total", ...defaultColDef, ...rightAlignColDef, ...floatFormater, width: 170, minWidth: 170, editable: (params) => params.data.type === "User Entry", resizable: true }
  ], [getPollutantTotal, emissionDetailOptions, organizationType]);

  const pollutantFields = useMemo(() => [
    "total_co2", "total_ch4", "total_n2o", "total_nox", "total_pm25", "total_pm10", "total_black_carbon"
  ], []);

  const hasTransportOrEnergy = useMemo(() => {
    return localEmissionDetails.some((ed) => {
      if (ed.type === "Transport" || ed.type === "Energy sources") return true;
      if (!ed.description) return false;
      const matches = findEmissionOptionMatches(emissionDetailOptions, ed.type, ed.category, ed.sub_category, ed.description);
      const match = (ed.unit ? matches.find((m) => m.unit?.toLowerCase().trim() === ed.unit?.toLowerCase().trim()) : null) || matches[0];
      return Boolean(
        match && (
          (match.co2 && parseFloat(match.co2) > 0) ||
          (match.ch4 && parseFloat(match.ch4) > 0) ||
          (match.n2o && parseFloat(match.n2o) > 0) ||
          (match.nox && parseFloat(match.nox) > 0) ||
          (match.pm25 && parseFloat(match.pm25) > 0) ||
          (match.pm10 && parseFloat(match.pm10) > 0) ||
          (match.black_carbon && parseFloat(match.black_carbon) > 0)
        )
      );
    });
  }, [localEmissionDetails, emissionDetailOptions]);

  const defaultCols = useMemo(() => [
    "calculation_method", "type", "category", "sub_category", "description",
    "quantity", "unit", "gwp_unit", "factor", "uncertainty", "totalco2eq",
    ...(hasTransportOrEnergy ? pollutantFields : []),
    "biogenic_total"
  ], [hasTransportOrEnergy, pollutantFields]);

  const isHidden = useCallback((field) => {
    // If it's a pollutant column:
    if (pollutantFields.includes(field)) {
      if (!hasTransportOrEnergy) {
        return true; // Always hidden when there is no Transport or Energy sources row
      }
      // When hasTransportOrEnergy is true, check custom user visibility
      const customVis = columnVisibilities[colVisibility_key];
      if (customVis && customVis.length > 0) {
        const hasAnyPollutantInCustomVis = customVis.some((f) => pollutantFields.includes(f));
        if (hasAnyPollutantInCustomVis) {
          return !customVis.includes(field);
        }
      }
      return false; // Default to automatically visible
    }

    const customVis = columnVisibilities[colVisibility_key];
    if (customVis && customVis.length > 0) {
      return !customVis.includes(field);
    }
    return !defaultCols.includes(field);
  }, [pollutantFields, hasTransportOrEnergy, columnVisibilities, colVisibility_key, defaultCols]);

  const columnDefs = useMemo(() => rawColumnDefs.map(c => ({ ...c, editable: readOnly ? false : c.editable, hide: isHidden(c.field) })), [rawColumnDefs, isHidden, readOnly]);

  // Synchronize AG Grid column visibility immediately whenever hasTransportOrEnergy or isHidden changes
  useEffect(() => {
    if (!gridRef.current?.api) return;
    pollutantFields.forEach((field) => {
      const shouldHide = isHidden(field);
      gridRef.current.api.setColumnVisible(field, !shouldHide);
    });
  }, [hasTransportOrEnergy, isHidden, pollutantFields]);

  useEffect(() => {
    setLocalEmissionDetails((prev) => {
      const isDirty = prev.some(ed => ed._isModified || ed._isNew) || detailIdsToBeDeleted.length > 0;
      if (!isDirty) {
        return emissionDetails.map(row => {
          const matches = findEmissionOptionMatches(emissionDetailOptions, row.type, row.category, row.sub_category, row.description);
          const match = (row.unit ? matches.find((m) => m.unit?.toLowerCase().trim() === row.unit?.toLowerCase().trim()) : null) || matches[0];
          return attachPollutantFactorsAndTotals({ ...row }, match);
        });
      }

      const deletedSet = new Set(detailIdsToBeDeleted);
      const newRows = prev.filter(p => p._isNew && !emissionDetails.some(ed => ed.id === p.id));
      const source = emissionDetails
        .filter(ed => !deletedSet.has(ed.id))
        .map(ed => {
          const localMod = prev.find(p => p.id === ed.id && p._isModified);
          return localMod || ed;
        });

      return [...source, ...newRows].map(row => {
        const matches = findEmissionOptionMatches(emissionDetailOptions, row.type, row.category, row.sub_category, row.description);
        const match = (row.unit ? matches.find((m) => m.unit?.toLowerCase().trim() === row.unit?.toLowerCase().trim()) : null) || matches[0];
        return attachPollutantFactorsAndTotals({ ...row }, match);
      });
    });

    if (gridRef.current?.api) {
      gridRef.current.api.refreshCells({ force: true });
    }
  }, [emissionDetails, emissionDetailOptions, detailIdsToBeDeleted]);

  useEffect(() => {
    if (!newlyAddedId || !gridRef.current) return;
    const api = gridRef.current.api;
    if (!api) return;
    api.paginationGoToLastPage();
    setTimeout(() => {
      const node = api.getRowNode(newlyAddedId);
      if (node) {
        api.ensureIndexVisible(node.rowIndex, "bottom");
        node.setSelected(true, true);
        setNewlyAddedId(null);
      }
    }, 50);
  }, [localEmissionDetails, newlyAddedId]);

  const onCellValueChanged = useCallback((params) => {
    const { colDef, oldValue, newValue, data } = params;
    let newData = { ...data, [colDef.field]: newValue };

    if (colDef.field === "type") {
      const leavingSupplyChainType = oldValue === SUPPLY_CHAIN_TYPE;
      newData = {
        ...newData,
        category: "", sub_category: "", description: "", unit: "", gwp_unit: 0, totalco2eq: 0, biogenic_total: 0,
        ...(leavingSupplyChainType ? { quantity: null } : {}),
      };
    } else if (colDef.field === "category") {
      if (newData.type !== "User Entry") {
        newData = {
          ...newData, sub_category: "", description: "", unit: "", gwp_unit: 0, totalco2eq: 0, biogenic_total: 0
        };
      }
    } else if (colDef.field === "sub_category") {
      if (newData.type !== "User Entry") {
        newData = {
          ...newData, description: "", unit: "", gwp_unit: 0, totalco2eq: 0, biogenic_total: 0
        };
      }
    } else if (colDef.field === "unit") {
      const { type, category, sub_category, description } = newData;
      const matches = findEmissionOptionMatches(emissionDetailOptions, type, category, sub_category, description);
      const match = matches.find((m) => m.unit?.toLowerCase().trim() === newValue?.toLowerCase().trim());

      // Auto-convert quantity when unit changes between convertible units
      if (oldValue && newValue && oldValue.toLowerCase().trim() !== newValue.toLowerCase().trim()) {
        try {
          const convertedQty = Convert(parseFloat(newData.quantity) || 0, oldValue, newValue);
          if (convertedQty && !isNaN(convertedQty) && convertedQty > 0) {
            newData.quantity = Math.round(convertedQty * 10000) / 10000;
          }
        } catch (e) {
          console.warn("Could not auto-convert quantity between units:", oldValue, newValue);
        }
      }

      if (match) {
        newData.gwp_unit = parseFloat(match.gwp_per_unit) || parseFloat(match["gwp_per_unit(kgco2)"]) || 0;
        newData = attachPollutantFactorsAndTotals(newData, match);
      } else if (matches.length > 0) {
        // Fallback to base option and scale factor by unit conversion ratio
        const baseMatch = matches[0];
        const baseGwp = parseFloat(baseMatch.gwp_per_unit) || parseFloat(baseMatch["gwp_per_unit(kgco2)"]) || 0;
        let unitScale = 1.0;
        try {
          const conv = Convert(1, newValue, baseMatch.unit);
          if (conv && !isNaN(conv) && conv > 0) unitScale = conv;
        } catch (e) {
          unitScale = 1.0;
        }
        newData.gwp_unit = Math.round(baseGwp * unitScale * 1000000) / 1000000;
        newData = attachPollutantFactorsAndTotals(newData, baseMatch);
      }
    } else if (colDef.field === "description") {
      const { type, category, sub_category } = newData;
      if (type === "User Entry") {
        if (newData.quantity === null || newData.quantity === undefined || newData.quantity === "") {
          newData.quantity = 1;
        }
        if (newData.factor === null || newData.factor === undefined || newData.factor === "") {
          newData.factor = 1.0;
        }
        if (newData.uncertainty === null || newData.uncertainty === undefined || newData.uncertainty === "") {
          newData.uncertainty = 1.0;
        }
      } else if (type === "By Product Supplier") {
        const match = supplierDataRef.current.find(
          (supplier) => (supplier.supplier ? `${supplier.supplier}_${supplier.name}` : supplier.name) === newValue
        );
        if (match) {
          newData.unit = match.functional_unit || "";
          newData.gwp_unit = match.gwp !== undefined ? parseFloat(match.gwp) : 0;
          if (newData.quantity === null || newData.quantity === undefined || newData.quantity === "") {
            newData.quantity = 1;
          }
          if (newData.factor === null || newData.factor === undefined || newData.factor === "") {
            newData.factor = 1.0;
          }
          if (newData.uncertainty === null || newData.uncertainty === undefined || newData.uncertainty === "") {
            newData.uncertainty = 1.0;
          }
        } else {
          newData.gwp_unit = 0;
        }
      } else if (type === SUPPLY_CHAIN_TYPE) {
        const scMatch = workspaceSupplierDataRef.current.find((item) => item.name === newValue);
        if (scMatch) {
          newData.unit = scMatch.functional_unit || "";
          newData.quantity = scMatch.quantity !== null && scMatch.quantity !== undefined ? scMatch.quantity : newData.quantity;
          const productMatch = supplierDataRef.current.find(
            (p) => p.name === newValue || (p.supplier && p.supplier === newValue)
          );
          if (productMatch && productMatch.gwp !== undefined) {
            newData.gwp_unit = parseFloat(productMatch.gwp) || 0;
          } else if (scMatch.gwp !== undefined && scMatch.gwp !== null) {
            const qtyVal = parseFloat(scMatch.quantity) || 1;
            newData.gwp_unit = parseFloat(scMatch.gwp) / qtyVal;
          } else {
            newData.gwp_unit = 0;
          }
          if (newData.factor === null || newData.factor === undefined || newData.factor === "") {
            newData.factor = 1.0;
          }
          if (newData.uncertainty === null || newData.uncertainty === undefined || newData.uncertainty === "") {
            newData.uncertainty = 1.0;
          }
        } else {
          newData.unit = "";
          newData.gwp_unit = 0;
        }
      } else {
        const matches = findEmissionOptionMatches(emissionDetailOptions, type, category, sub_category, newValue);
        const match = (newData.unit ? matches.find((m) => m.unit?.toLowerCase().trim() === newData.unit?.toLowerCase().trim()) : null) || matches[0];
        if (match) {
          newData.unit = match.unit || "";
          newData.gwp_unit = parseFloat(match.gwp_per_unit) || parseFloat(match["gwp_per_unit(kgco2)"]) || 0;

          if (newData.quantity === null || newData.quantity === undefined || newData.quantity === "") {
            newData.quantity = 1;
          }
          if (newData.factor === null || newData.factor === undefined || newData.factor === "") {
            newData.factor = 1.0;
          }
          if (newData.uncertainty === null || newData.uncertainty === undefined || newData.uncertainty === "") {
            newData.uncertainty = 1.0;
          }
          newData = attachPollutantFactorsAndTotals(newData, match);
        } else {
          newData.gwp_unit = 0;
        }
      }
    }

    // Recalculate GWP: quantity * factor * uncertainty * gwp_unit
    const qty = (newData.quantity !== null && newData.quantity !== undefined && newData.quantity !== "") ? (isNaN(parseFloat(newData.quantity)) ? 0 : parseFloat(newData.quantity)) : 0;
    const factor = (newData.factor !== null && newData.factor !== undefined && newData.factor !== "") ? (isNaN(parseFloat(newData.factor)) ? 1.0 : parseFloat(newData.factor)) : 1.0;
    const uncertainty = (newData.uncertainty !== null && newData.uncertainty !== undefined && newData.uncertainty !== "") ? (isNaN(parseFloat(newData.uncertainty)) ? 1.0 : parseFloat(newData.uncertainty)) : 1.0;
    const gwpUnitVal = parseFloat(newData.gwp_unit) || 0;

    const computed = qty * factor * uncertainty * gwpUnitVal;
    if (newData.type === "User Entry") {
      newData.totalco2eq = computed;
      if (colDef.field === "biogenic_total") {
        newData.biogenic_total = isNaN(parseFloat(newValue)) ? 0 : parseFloat(newValue);
      }
    } else {
      const isBiogenicRow = newData.sub_category?.toLowerCase().includes('biogenic') && gwpUnitVal < 0;
      newData.totalco2eq = isBiogenicRow ? 0 : computed;
      newData.biogenic_total = isBiogenicRow ? computed : 0;
    }

    // Recompute all 7 pollutant totals on every cell change with option lookup fallback
    const curMatches = findEmissionOptionMatches(emissionDetailOptions, newData.type, newData.category, newData.sub_category, newData.description);
    const curMatch = (newData.unit ? curMatches.find((m) => m.unit?.toLowerCase().trim() === newData.unit?.toLowerCase().trim()) : null) || curMatches[0];
    newData = attachPollutantFactorsAndTotals(newData, curMatch);

    newData._isModified = true;
    newData._version = (newData._version || 0) + 1;

    setLocalEmissionDetails((prev) => prev.map((item) => (item.id === newData.id ? newData : item)));
    setIsSaveVisible(true);
    params.api.refreshCells({ rowNodes: [params.node], force: true });
  }, [emissionDetailOptions, organizationType, setIsSaveVisible]);

  useEffect(() => {
    if (organizationType !== Supply_chain.label) {
      Supply_chain.get_from_my_workspace()
        .then((res) => res.json())
        .then((data) => {
          const supplyChains = data || [];
          if (supplyChains.length === 0) {
            workspaceSupplierDataRef.current = [];
            return;
          }
          const ids = supplyChains.map((s) => s.id);
          Supply_chain.get_total_gwp_by_scope(ids)
            .then((scopeRes) => scopeRes.json())
            .then((scopeData) => {
              const fossilMap = {};
              if (Array.isArray(scopeData)) {
                scopeData.forEach((d) => {
                  fossilMap[d.id] = (Number(d.scope_1) || 0) + (Number(d.scope_2) || 0) + (Number(d.scope_3) || 0);
                });
              }
              workspaceSupplierDataRef.current = supplyChains.map((sc) => ({
                ...sc,
                gwp: fossilMap[sc.id] !== undefined ? fossilMap[sc.id] : sc.gwp,
              }));
            })
            .catch((err) => {
              console.error("Error fetching scope data for workspace supply chains:", err);
              workspaceSupplierDataRef.current = supplyChains;
            });
        })
        .catch((error) => console.error("Error fetching workspace supply chains:", error));
    }
  }, [refresh, organizationType]);

  // Expose save handlers to parent (OrganizationScope) via saveRef
  useEffect(() => {
    if (saveRef) {
      saveRef.current = {
        stopEditing: () => {
          gridRef.current?.api?.stopEditing();
        },
        getSavePayload: () => {
          const currentList = localEmissionDetailsRef.current || localEmissionDetails;
          const toSave = currentList
            .filter((ed) => ed._isModified || ed._isNew)
            .map((ed) => {
              const { _version, ...clean } = ed;
              return {
                ...clean,
                factor: (clean.factor !== null && clean.factor !== undefined && clean.factor !== "") ? parseFloat(clean.factor) : 1.0,
                uncertainty: (clean.uncertainty !== null && clean.uncertainty !== undefined && clean.uncertainty !== "") ? parseFloat(clean.uncertainty) : 1.0,
                quantity: (clean.quantity !== null && clean.quantity !== undefined && clean.quantity !== "") ? parseFloat(clean.quantity) : 0.0,
                gwp_unit: (clean.gwp_unit !== null && clean.gwp_unit !== undefined && clean.gwp_unit !== "") ? parseFloat(clean.gwp_unit) : 0.0,
                totalco2eq: (clean.totalco2eq !== null && clean.totalco2eq !== undefined && clean.totalco2eq !== "") ? parseFloat(clean.totalco2eq) : 0.0,
                biogenic_total: (clean.biogenic_total !== null && clean.biogenic_total !== undefined && clean.biogenic_total !== "") ? parseFloat(clean.biogenic_total) : 0.0,
                is_new: Boolean(clean._isNew),
                is_modified: Boolean(clean._isModified),
              };
            });
          return {
            toSave,
            toDelete: detailIdsToBeDeleted,
          };
        },
        onSaveSuccess: (payload) => {
          const savedIds = new Set(payload.toSave.map((ed) => ed.id));
          const deletedIds = new Set(payload.toDelete);
          setLocalEmissionDetails((prev) =>
            prev
              .filter((ed) => !deletedIds.has(ed.id))
              .map((ed) => {
                if (savedIds.has(ed.id)) {
                  const { _isNew, _isModified, ...clean } = ed;
                  return clean;
                }
                return ed;
              })
          );
          setDetailIdsToBeDeleted([]);
        },
      };
    }
  }, [saveRef, localEmissionDetails, detailIdsToBeDeleted]);

  const totalBiogenicFromRows = useMemo(
    () => localEmissionDetails.reduce((sum, item) => sum + (Number(item.biogenic_total) || 0), 0),
    [localEmissionDetails]
  );

  const totalFossilFromRows = useMemo(
    () => localEmissionDetails.reduce((sum, item) => sum + (Number(item.totalco2eq) || 0), 0),
    [localEmissionDetails]
  );

  const totalFossil = !isBiogenic && !isAvoided ? totalFossilFromRows : 0;
  const totalBiogenic = totalBiogenicFromRows;

  useEffect(() => {
    onInventoryTotalChange?.(totalFossil);
  }, [totalFossil, onInventoryTotalChange, emissionID]);

  useEffect(() => {
    onInventoryBiogenicTotalChange?.(totalBiogenic);
  }, [totalBiogenic, onInventoryBiogenicTotalChange, emissionID]);

  // Recomputed totals can legitimately differ from the stored scope total just from
  // selecting a row (rounding, load timing) — only treat it as save-worthy when the
  // currently selected emission actually has a real local edit behind it.
  const hasPendingDetailEdits = useMemo(
    () => localEmissionDetails.some((ed) => ed.emission_id === emissionID && (ed._isModified || ed._isNew)),
    [localEmissionDetails, emissionID]
  );

  useEffect(() => {
    onPendingEditsChange?.(hasPendingDetailEdits);
  }, [hasPendingDetailEdits, onPendingEditsChange]);

  const footerRight = (
    <Box sx={{ display: "flex", gap: 3, alignItems: "center", pr: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? "#a1a1aa" : "#64748b" }}>
        Total Biogenic: <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: totalBiogenic < 0 ? "#659d33" : (isDark ? "#ffffff" : "#0f172a") }}>{totalBiogenic.toLocaleString("en-CA", { maximumFractionDigits: 2 })}</Typography>
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? "#a1a1aa" : "#64748b" }}>
        Total Fossil: <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: totalFossil < 0 ? "#659d33" : (isDark ? "#ffffff" : "#0f172a") }}>{totalFossil.toLocaleString("en-CA", { maximumFractionDigits: 2 })}</Typography>
      </Typography>
    </Box>
  );

  const handleExportXLSX = useCallback(() => {
    if (!localEmissionDetails || localEmissionDetails.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(localEmissionDetails);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Emission Details");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, "emission_details.xlsx");
  }, [localEmissionDetails]);

  return (
    <>
      <DataTableCard
        title={organizationType === Supply_chain.label ? t("Inventory") : t("Environmental Data Records")}
        actions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", mr: 1, ...(readOnly ? { pointerEvents: "none", opacity: 0.5 } : {}) }}>
              <MaterialSearchInput onSelect={onSearchSelect} searchType="emission_detail" />
            </Box>
            <M3Button
              size="sm"
              variant="filled"
              icon={<FiPlus size={16} />}
              onClick={newEmissionDetail}
              disabled={readOnly || !emissionID}
            >
              {t("Add Record")}
            </M3Button>
            <Tooltip title={t("Manage Columns")}>
              <span ref={gridHeaderContainerRef} style={{ display: "inline-flex" }}>
                <M3IconButton
                  size="sm"
                  icon={<LuFilter size={16} />}
                  onClick={() => setColumnMenuVisible((p) => !p)}
                  aria-label={t("Manage Columns")}
                  style={{
                    background: "var(--m3-primary-container)",
                    color: "var(--m3-on-primary-container)",
                  }}
                />
              </span>
            </Tooltip>
            <Tooltip title={t("Remove Selected")}>
              <span>
                <M3IconButton
                  size="sm"
                  icon={<FiMinus size={18} />}
                  onClick={deleteSelected}
                  disabled={readOnly || selectedEmissionDetails.length === 0}
                  aria-label={t("Remove Selected")}
                  style={{
                    background: (readOnly || selectedEmissionDetails.length === 0) ? "rgba(239, 68, 68, 0.06)" : "rgba(239, 68, 68, 0.15)",
                    color: (readOnly || selectedEmissionDetails.length === 0) ? "rgba(239, 68, 68, 0.35)" : "#dc2626",
                  }}
                />
              </span>
            </Tooltip>
            <Tooltip title={t("Download XLSX")}>
              <span>
                <M3IconButton
                  size="sm"
                  icon={<LuDownload size={16} />}
                  onClick={handleExportXLSX}
                  aria-label={t("Download XLSX")}
                  style={{
                    background: "var(--m3-primary-container)",
                    color: "var(--m3-on-primary-container)",
                  }}
                />
              </span>
            </Tooltip>
            <Tooltip title={t("Refresh")}>
              <span>
                <M3IconButton
                  size="sm"
                  icon={<FiRefreshCw size={16} />}
                  onClick={() => setRefresh((r) => !r)}
                  aria-label={t("Refresh")}
                  style={{
                    background: "var(--m3-primary-container)",
                    color: "var(--m3-on-primary-container)",
                  }}
                />
              </span>
            </Tooltip>
          </Box>
        }
      >
        <AgGrid
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={localEmissionDetails}
          onSelectionChanged={(e) => setSelectedEmissionDetails(e.api.getSelectedRows())}
          footerRightContent={footerRight}
          onCellValueChanged={onCellValueChanged}
        />
      </DataTableCard>
      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={rawColumnDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
      />
    </>
  );
};

export default EmissionDetailGrid;
